import { useEffect, useMemo, useRef, useState } from 'react';
import structuredClone from '@ungap/structured-clone';
import { ThemeProvider } from 'styled-components';
import merge from 'lodash/merge';

import { ALERT_POSITION, ALERT_LEVELS, IAlert, ITestable } from '@belong/types';
import { useAnalyticsEvents } from '@belong/providers/analyticsEvents';
import { ANALYTICS_STRINGS } from '@belong/analytics';
import { BUTTON_VARIANTS } from '@belong/themes';
import { ALERT, ALERT_DISMISS_BUTTON, ALERT_LINK } from '../../../helpers/testIds';
import {
  AlertFocus,
  CrossIcon,
  DismissButton,
  Icon,
  Link,
  MaxWidthContainer,
  Root,
  TextWrapper,
  iconMapping,
  themeMapping
} from './Alert.styles';
import { storeDismissedAlertCode, hasAlertBeenDismissed } from './Alert.cookies';

const Alert: React.FC<IAlert & ITestable> = ({
  children,
  className,
  innerRef,
  hasFocus,
  hasPadding,
  link,
  isDismissable,
  onDismiss,
  position,
  role,
  type,
  code,
  id,
  dismissedCookieId,
  persistCookie,
  dismissed,
  'data-testid': dataTestId = ALERT,
  asNotification,
  ...otherProps
}) => {
  const focusRef = useRef<HTMLDivElement>(null);
  const icon = useMemo(() => iconMapping.get(type as ALERT_LEVELS), [type]);
  const themeColors = useMemo(() => themeMapping.get(type)!, [type]);
  const hasBackground = position !== ALERT_POSITION.INLINE;
  const analyticsEvents = useAnalyticsEvents();

  // isDismissed is used to track the visibility of the alert
  // dimissed value has been obtained in the server
  // If dismissed is undefined, fallback to check if dismissedCookieId exists to later decide based on cookies
  const [isDismissed, setIsDismissed] = useState<boolean>(
    dismissed !== undefined ? dismissed : dismissedCookieId !== undefined
  );

  const handleDismiss = () => {
    // If onDismiss callback is provided, call it
    if (onDismiss) {
      onDismiss();
    }

    setIsDismissed(true);

    if (dismissedCookieId) {
      analyticsEvents.send({
        category: ANALYTICS_STRINGS.CATEGORY.PAGE_ENGAGEMENT,
        action: ANALYTICS_STRINGS.ACTION.CLICK,
        label: dismissedCookieId
      });

      storeDismissedAlertCode(dismissedCookieId, persistCookie);
    }
  };

  const shouldRenderDismissButton = isDismissable && position !== ALERT_POSITION.INLINE;

  const dismissButton = shouldRenderDismissButton && (
    <DismissButton
      onClick={handleDismiss}
      aria-label="Close alert"
      data-testid={ALERT_DISMISS_BUTTON}
      canExpand={position === ALERT_POSITION.GLOBAL}
    >
      <CrossIcon />
    </DismissButton>
  );

  useEffect(() => {
    if (dismissed !== undefined) {
      // The server already told us, trust it.
      return;
    }
    // Check cookie status after component mounts
    if (dismissedCookieId) {
      setIsDismissed(hasAlertBeenDismissed(dismissedCookieId, persistCookie));
    } else {
      setIsDismissed(false);
    }
  }, [dismissed, dismissedCookieId, persistCookie]);

  useEffect(() => {
    if (focusRef?.current !== null && hasFocus && !isDismissed) {
      focusRef.current.focus();
    }
  }, [hasFocus, isDismissed]);

  // If `hasFocus` is true we don't want the role to be alert as it may have adverse effects
  const alertRole = role ?? 'alert';
  const roleToApply = (alertRole === 'alert' && hasFocus) || asNotification ? undefined : alertRole;

  // Check if alert should be hidden based on cookie
  if (isDismissed) {
    return null;
  }

  return (
    <ThemeProvider theme={theme => merge(structuredClone(theme), themeColors)}>
      <AlertFocus ref={focusRef} tabIndex={-1} data-alert-type={type} data-code={code} data-testid={dataTestId}>
        <Root
          className={`${className} alert`}
          role={roleToApply}
          withBackground={hasBackground}
          withDismissButton={!!shouldRenderDismissButton}
          withColoredGutter={position === ALERT_POSITION.BLOCK}
          isCentered={position === ALERT_POSITION.GLOBAL}
          hasCurvedBorder={position === ALERT_POSITION.BLOCK}
          canExpand={position === ALERT_POSITION.GLOBAL}
          ref={innerRef}
          {...otherProps}
        >
          {icon && <Icon name={icon} hasColor={themeColors.foregroundColor} aria-hidden="true" />}
          <TextWrapper isCentered={position === ALERT_POSITION.GLOBAL}>
            {children}
            {link && <Link data-testid={ALERT_LINK} {...link} variant={BUTTON_VARIANTS.QUATERNARY} />}
          </TextWrapper>
          {shouldRenderDismissButton &&
            (position === ALERT_POSITION.GLOBAL ? (
              <MaxWidthContainer constrainWidth>{dismissButton}</MaxWidthContainer>
            ) : (
              dismissButton
            ))}
        </Root>
      </AlertFocus>
    </ThemeProvider>
  );
};

Alert.displayName = 'Alert';
export default Alert;
